import { useState, useEffect, useCallback } from 'react';
import {
    Badge,
    Box,
    Chip,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { Bell, Check, UserPlus, Bug, Megaphone } from 'lucide-react';
import { useGetIdentity } from 'react-admin';
import { supabase } from '../providers/supabase/supabase';
import type { BugReport } from './types';

const POLL_INTERVAL = 30_000; // 30 seconds

interface UnreadUpdate {
    id: string;
    subject: string;
    created_at: string;
}

export default function NotificationBell() {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [resolved, setResolved] = useState<BugReport[]>([]);
    const [pendingUsers, setPendingUsers] = useState(0);
    const [openBugs, setOpenBugs] = useState(0);
    const [unreadUpdates, setUnreadUpdates] = useState<UnreadUpdate[]>([]);
    const { identity } = useGetIdentity();
    const isAdmin = identity?.administrator === true;

    const fetchResolved = useCallback(async () => {
        // Use auth user ID directly (identity.id is the sales record ID)
        const {
            data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const { data, error } = await supabase
            .from('bug_reports')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'complete')
            .eq('resolved_read', false)
            .order('updated_at', { ascending: false });

        if (!error && data) {
            setResolved(data as BugReport[]);
        }
    }, []);

    const fetchUnreadUpdates = useCallback(async () => {
        const {
            data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        // Get all product updates
        const { data: allUpdates } = await supabase
            .from('product_updates')
            .select('id, subject, created_at')
            .order('created_at', { ascending: false });

        if (!allUpdates || allUpdates.length === 0) {
            setUnreadUpdates([]);
            return;
        }

        // Get which ones this user has read
        const { data: reads } = await supabase
            .from('product_update_reads')
            .select('update_id')
            .eq('user_id', user.id);

        const readIds = new Set((reads ?? []).map(r => r.update_id));
        setUnreadUpdates(allUpdates.filter(u => !readIds.has(u.id)));
    }, []);

    const fetchAdminNotifications = useCallback(async () => {
        if (!isAdmin) return;

        // Count pending users (approved === false)
        const { count: userCount } = await supabase
            .from('sales')
            .select('*', { count: 'exact', head: true })
            .eq('approved', false);
        setPendingUsers(userCount ?? 0);

        // Count open bugs (status != 'complete')
        const { count: bugCount } = await supabase
            .from('bug_reports')
            .select('*', { count: 'exact', head: true })
            .neq('status', 'complete');
        setOpenBugs(bugCount ?? 0);
    }, [isAdmin]);

    // Poll for resolved bugs + unread updates
    useEffect(() => {
        fetchResolved();
        fetchUnreadUpdates();
        const interval = setInterval(() => {
            fetchResolved();
            fetchUnreadUpdates();
        }, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchResolved, fetchUnreadUpdates]);

    // Poll for admin notifications
    useEffect(() => {
        fetchAdminNotifications();
        const interval = setInterval(fetchAdminNotifications, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchAdminNotifications]);

    const handleMarkRead = async (id: string) => {
        await supabase
            .from('bug_reports')
            .update({ resolved_read: true })
            .eq('id', id);

        setResolved(prev => prev.filter(b => b.id !== id));
    };

    const handleMarkUpdateRead = async (updateId: string) => {
        const {
            data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        await supabase
            .from('product_update_reads')
            .upsert(
                { user_id: user.id, update_id: updateId },
                { onConflict: 'user_id,update_id' }
            );
        setUnreadUpdates(prev => prev.filter(u => u.id !== updateId));
    };

    const handleMarkAllRead = async () => {
        // Mark bug reports read
        const bugIds = resolved.map(b => b.id);
        if (bugIds.length > 0) {
            await supabase
                .from('bug_reports')
                .update({ resolved_read: true })
                .in('id', bugIds);
        }

        // Mark product updates read
        const {
            data: { user },
        } = await supabase.auth.getUser();
        if (user && unreadUpdates.length > 0) {
            const rows = unreadUpdates.map(u => ({
                user_id: user.id,
                update_id: u.id,
            }));
            await supabase
                .from('product_update_reads')
                .upsert(rows, { onConflict: 'user_id,update_id' });
        }

        setResolved([]);
        setUnreadUpdates([]);
        setAnchorEl(null);
    };

    const adminCount = isAdmin ? pendingUsers + openBugs : 0;
    const count = resolved.length + unreadUpdates.length + adminCount;

    return (
        <>
            <IconButton
                onClick={e => setAnchorEl(e.currentTarget)}
                sx={{ color: '#64748b' }}
            >
                <Badge
                    badgeContent={count}
                    color="error"
                    max={9}
                    sx={{
                        '& .MuiBadge-badge': {
                            fontSize: '0.65rem',
                            minWidth: 16,
                            height: 16,
                        },
                    }}
                >
                    <Bell size={20} />
                </Badge>
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                    sx: {
                        borderRadius: '8px',
                        mt: 1,
                        minWidth: 300,
                        maxHeight: 400,
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                        }}
                    >
                        Notifications
                    </Typography>
                    {count > 0 && (
                        <Typography
                            component="button"
                            onClick={handleMarkAllRead}
                            sx={{
                                fontSize: '0.75rem',
                                color: '#10b981',
                                cursor: 'pointer',
                                border: 'none',
                                background: 'none',
                                fontWeight: 500,
                                '&:hover': {
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Mark all read
                        </Typography>
                    )}
                </Box>
                <Divider />

                {isAdmin && pendingUsers > 0 && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            window.location.hash = '#/admin-panel';
                        }}
                        sx={{
                            py: 1.5,
                            px: 2,
                            gap: 1.5,
                        }}
                    >
                        <UserPlus
                            size={16}
                            color="#f59e0b"
                            style={{ flexShrink: 0 }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                }}
                            >
                                {pendingUsers} pending user
                                {pendingUsers > 1 ? 's' : ''} awaiting approval
                            </Typography>
                        </Box>
                        <Chip
                            label={pendingUsers}
                            size="small"
                            color="warning"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    </MenuItem>
                )}

                {isAdmin && openBugs > 0 && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            window.location.hash = '#/admin-panel?tab=bugs';
                        }}
                        sx={{
                            py: 1.5,
                            px: 2,
                            gap: 1.5,
                        }}
                    >
                        <Bug
                            size={16}
                            color="#ef4444"
                            style={{ flexShrink: 0 }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                }}
                            >
                                {openBugs} open bug
                                {openBugs > 1 ? 's' : ''}
                            </Typography>
                        </Box>
                        <Chip
                            label={openBugs}
                            size="small"
                            color="error"
                            sx={{ fontSize: '0.7rem' }}
                        />
                    </MenuItem>
                )}

                {/* Unread product updates */}
                {unreadUpdates.length > 0 &&
                    (adminCount > 0 || resolved.length > 0) && <Divider />}
                {unreadUpdates.length > 0 && (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            window.location.hash = '#/support';
                            // Small delay to let navigation happen,
                            // then click Product Updates tab
                            setTimeout(() => {
                                const tabs =
                                    document.querySelectorAll('[role="tab"]');
                                const updatesTab = Array.from(tabs).find(t =>
                                    t.textContent?.includes('Product Updates')
                                ) as HTMLElement | undefined;
                                updatesTab?.click();
                            }, 100);
                        }}
                        sx={{
                            py: 1.5,
                            px: 2,
                            gap: 1.5,
                        }}
                    >
                        <Megaphone
                            size={16}
                            color="#10b981"
                            style={{ flexShrink: 0 }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    fontWeight: 500,
                                }}
                            >
                                {unreadUpdates.length} new product update
                                {unreadUpdates.length > 1 ? 's' : ''}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    color: '#64748b',
                                    mt: 0.25,
                                    lineHeight: 1.4,
                                }}
                            >
                                {unreadUpdates[0].subject}
                                {unreadUpdates.length > 1
                                    ? ` and ${unreadUpdates.length - 1} more`
                                    : ''}
                            </Typography>
                        </Box>
                        <Chip
                            label={unreadUpdates.length}
                            size="small"
                            sx={{
                                bgcolor: '#ecfdf5',
                                color: '#10b981',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                            }}
                        />
                    </MenuItem>
                )}

                {isAdmin && adminCount > 0 && resolved.length > 0 && (
                    <Divider />
                )}

                {count === 0 ? (
                    <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                        <Typography
                            sx={{
                                fontSize: '0.875rem',
                                color: '#94a3b8',
                            }}
                        >
                            No new notifications
                        </Typography>
                    </Box>
                ) : resolved.length === 0 ? null : (
                    resolved.map(bug => (
                        <MenuItem
                            key={bug.id}
                            onClick={() => handleMarkRead(bug.id)}
                            sx={{
                                py: 1.5,
                                px: 2,
                                alignItems: 'flex-start',
                                gap: 1.5,
                                whiteSpace: 'normal',
                            }}
                        >
                            <Check
                                size={16}
                                color="#10b981"
                                style={{
                                    flexShrink: 0,
                                    marginTop: 2,
                                }}
                            />
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.8125rem',
                                        fontWeight: 500,
                                        color: '#0f172a',
                                    }}
                                >
                                    Bug resolved
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: '#64748b',
                                        mt: 0.25,
                                        lineHeight: 1.4,
                                    }}
                                >
                                    {bug.description.length > 80
                                        ? `${bug.description.slice(0, 80)}...`
                                        : bug.description}
                                </Typography>
                                {bug.resolution_note && (
                                    <Box
                                        sx={{
                                            mt: 0.75,
                                            px: 1.5,
                                            py: 1,
                                            bgcolor: '#f0fdf4',
                                            borderRadius: '6px',
                                            borderLeft: '3px solid #10b981',
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '0.75rem',
                                                color: '#15803d',
                                                fontStyle: 'italic',
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            {bug.resolution_note}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </MenuItem>
                    ))
                )}
            </Menu>
        </>
    );
}
